import type { ArtMetadata, ArtTextStyle, Bind, Slot } from "../schedule-presets/art-metadata";
import type { MetadataEventPayload } from "../metadata-history/metadata-events";
import type { MetadataDay } from "../metadata-history/metadata-targets";
import type { StreamLayout } from "./day-layouts";
import { defaultElementPosition } from "../schedule-art/layout-coordinates";
import { weekdayKeys } from "../schedule-time/schedule-dates";

export type StreamNumber = 1 | 2;

export function streamBindings(number: StreamNumber): Bind[] {
  return ["title", "subtitle", "time", "platform_icon", "member_only"].map(field => `slot${number}.${field}` as Bind);
}

export function requiredStreamBindings(number: StreamNumber): Bind[] {
  return [`slot${number}.title`, `slot${number}.time`];
}

export function createStreamElement(metadata: ArtMetadata, mode: StreamLayout["mode"], bind: Bind, titleOffset: number[], day?: MetadataDay) {
  const field = bind.split(".")[1]!;
  const name = `builder_${mode}_${day ? `${day}_` : ""}${bind.replace(".", "_")}`;
  const styles: Record<string, ArtTextStyle> = {};
  const offsets: Record<string, number[]> = {
    title: titleOffset,
    subtitle: [titleOffset[0]!, titleOffset[1]! + 52],
    time: [titleOffset[0]!, titleOffset[1]! - 40],
    platform_icon: [titleOffset[0]! + 110, titleOffset[1]! - 40],
    member_only: [titleOffset[0]!, titleOffset[1]! + 90],
  };
  const element: Slot = { bind, offset: offsets[field]! };
  if (field === "platform_icon") return { element: { ...element, type: "icon" as const, scale: 75 }, styles };
  const source = field === "member_only" ? metadata.styles.member_only ?? metadata.styles.stream_subtitle : metadata.styles[`stream_${field}`]!;
  styles[name] = { ...source, size: field === "title" ? 40 : 26, align: "left" };
  element.style = name;
  if (field === "time") element.format = mode === "one_slot" ? "builder_stream_time" : `${name}_format`;
  return { element, styles };
}

export function toggleStreamElement(metadata: ArtMetadata, layout: StreamLayout, day: MetadataDay, bind: Bind, enabled: boolean): MetadataEventPayload {
  const number: StreamNumber = bind.startsWith("slot2.") ? 2 : 1;
  if (!enabled && requiredStreamBindings(number).includes(bind)) {
    return { type: "day-layout/set", layout, styles: {}, action: "element" };
  }
  const next = structuredClone(layout);
  const custom = !!next.exceptions[day];
  const elements = next.exceptions[day] ?? next.shared;
  const kept = elements.filter(element => element.bind !== bind);
  const styles: Record<string, ArtTextStyle> = {};
  const formats: Record<string, string> = {};
  if (enabled) {
    const titleBind = `slot${number}.title`;
    const title = elements.find(element => element.bind === titleBind);
    const titleOffset = title?.offset ?? layout.shared.find(element => element.bind === titleBind)?.offset ?? defaultElementPosition([24, number === 1 ? 120 : 320]);
    const added = createStreamElement(metadata, layout.mode, bind, titleOffset, custom ? day : undefined);
    // Re-enabling keeps any style definition the user already edited.
    if (added.element.style && metadata.styles[added.element.style]) added.styles[added.element.style] = metadata.styles[added.element.style]!;
    if (added.element.format) formats[added.element.format] = metadata.formats[added.element.format] ?? "%H:%M";
    kept.push(added.element);
    Object.assign(styles, added.styles);
  }
  const bindings = [...streamBindings(1), ...streamBindings(2)];
  kept.sort((a, b) => bindings.indexOf(a.bind) - bindings.indexOf(b.bind));
  if (custom) next.exceptions[day] = kept;
  else next.shared = kept;
  return { type: "day-layout/set", layout: next, styles, formats, action: "element", day: custom ? day : undefined };
}

export function streamLayoutComplete(metadata: ArtMetadata | undefined, mode: StreamLayout["mode"]) {
  const required = mode === "one_slot" ? requiredStreamBindings(1) : [...requiredStreamBindings(1), ...requiredStreamBindings(2)];
  return !!metadata && weekdayKeys.every(day => required.every(bind => metadata.days[day][mode].some(element => element.bind === bind)));
}
